import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './HeroSlider.css';

interface VideoSlide {
  src: string;
  thumb: string;
  titleKey: string;
}

const slides: VideoSlide[] = [
  { src: '/videos/proje-1.mp4', thumb: '/video1kapak.png', titleKey: 'projects.videos.intro.title' },
  { src: '/videos/proje-2.mp4', thumb: '/video2kapak.png', titleKey: 'projects.videos.design.title' },
  { src: '/videos/proje-3.mp4', thumb: '/video3kapak.png', titleKey: 'projects.videos.multipurpose.title' },
  { src: '/videos/proje-4.mp4', thumb: '/video4kapak.png', titleKey: 'projects.videos.privateSchool.title' },
  { src: '/videos/proje-5.mp4', thumb: '/video5kapak.png', titleKey: 'projects.videos.economicSchool.title' },
];

const AUTO_MS = 7000;

const HeroSlider: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [hoverPaused, setHoverPaused] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const go = useCallback((i: number) => {
    const len = slides.length;
    setActive(((i % len) + len) % len);
  }, []);
  const nextSlide = useCallback(() => setActive((a) => (a + 1) % slides.length), []);
  const prevSlide = useCallback(() => setActive((a) => (a - 1 + slides.length) % slides.length), []);

  // Auto-advance only while no video is playing and the slider isn't hovered.
  useEffect(() => {
    if (hoverPaused || playingIndex !== null) return;
    timer.current = setTimeout(() => nextSlide(), AUTO_MS);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [active, hoverPaused, playingIndex, nextSlide]);

  // When the slide changes, reset/pause every video and clear the playing state
  // so the next slide shows its thumbnail again.
  useEffect(() => {
    videoRefs.current.forEach((v) => {
      if (v) { v.pause(); v.currentTime = 0; }
    });
    setPlayingIndex(null);
  }, [active]);

  const playVideo = (i: number) => {
    const v = videoRefs.current[i];
    if (!v) return;
    v.play().then(() => setPlayingIndex(i)).catch(() => {});
  };

  return (
    <section
      className="nishhero"
      onMouseEnter={() => setHoverPaused(true)}
      onMouseLeave={() => setHoverPaused(false)}
    >
      <div className="hero-track" style={{ transform: `translateX(-${active * 100}%)` }}>
        {slides.map((slide, i) => {
          const isPlaying = playingIndex === i;
          return (
            <div className="nishhero-slide" key={i}>
              <div className="hero-video-slide">
                <video
                  ref={(el) => { videoRefs.current[i] = el; }}
                  className="hero-video"
                  src={slide.src}
                  poster={slide.thumb}
                  playsInline
                  preload="none"
                  controls={isPlaying}
                  onEnded={() => setPlayingIndex(null)}
                />

                {!isPlaying && (
                  <>
                    <div className="hero-video-overlay" />
                    <button
                      className="hero-play-btn"
                      onClick={() => playVideo(i)}
                      aria-label="Play video"
                    >
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </button>
                    <div className="hero-video-caption">
                      <div className="hero-eyebrow">
                        <span className="hero-eyebrow-bar" />
                        {t('projects.badge')}
                      </div>
                      <h2>{t(slide.titleKey)}</h2>
                      <p className="hero-video-desc">{t(slide.titleKey.replace('.title', '.description'))}</p>
                      <button className="hero-video-link" onClick={() => navigate('/projects')}>
                        <span>{t('common.viewAll', { defaultValue: 'View All Projects' })}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Arrows */}
      <button className="hero-arrow left" onClick={prevSlide} aria-label="Previous">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button className="hero-arrow right" onClick={nextSlide} aria-label="Next">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      {/* Dots */}
      <div className="hero-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`hero-dot ${i === active ? 'active' : ''}`}
            onClick={() => go(i)}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
